import { GOLD, CARD_BG, BORDER, TEXT, MUTED, GREEN, RED, DARK_BG } from '../constants';
import { fmt, fmtDec } from '../utils';

// ── BASE INPUT STYLES ──────────────────────────────────────────────────────
export const inputStyle = {
  background: '#0D1117', border: `1px solid ${BORDER}`,
  color: TEXT, padding: '8px 10px', borderRadius: 6,
  fontSize: 12, width: '100%', fontFamily: "'DM Mono', monospace",
  outline: 'none',
};
export const inputSmStyle = { ...inputStyle, padding: '6px 8px', fontSize: 11 };

export function Input({ style, ...props }) {
  return <input style={{ ...inputStyle, ...style }} {...props} />;
}
export function Select({ children, style, ...props }) {
  return (
    <select style={{ ...inputStyle, ...style, cursor: 'pointer' }} {...props}>
      {children}
    </select>
  );
}
export function Textarea({ style, ...props }) {
  return <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, ...style }} {...props} />;
}

// ── FORM FIELD ─────────────────────────────────────────────────────────────
export function FormField({ label, children, style, flex = 1, minWidth = 80 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex, minWidth, ...style }}>
      <label style={{ fontSize: 9, color: MUTED, letterSpacing: 1.5, textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
}

// ── BUTTONS ────────────────────────────────────────────────────────────────
export function PrimaryBtn({ children, style, ...props }) {
  return (
    <button style={{
      background: GOLD, color: '#0A0C0E', border: 'none',
      padding: '9px 20px', borderRadius: 6, cursor: 'pointer',
      fontSize: 12, fontWeight: 500, fontFamily: "'DM Mono', monospace",
      letterSpacing: 0.5, whiteSpace: 'nowrap', ...style,
    }} {...props}>{children}</button>
  );
}
export function GhostBtn({ children, style, active, ...props }) {
  return (
    <button style={{
      background: 'transparent',
      color: active ? GOLD : MUTED,
      border: `1px solid ${active ? GOLD : BORDER}`,
      padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
      fontSize: 11, fontFamily: "'DM Mono', monospace", letterSpacing: 0.5,
      whiteSpace: 'nowrap', ...style,
    }} {...props}>{children}</button>
  );
}
export function DangerBtn({ children, style, ...props }) {
  return (
    <button style={{
      background: 'transparent', color: RED, border: `1px solid ${RED}`,
      padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
      fontSize: 10, fontFamily: "'DM Mono', monospace", ...style,
    }} {...props}>{children}</button>
  );
}

// ── CARD / SECTION ─────────────────────────────────────────────────────────
export function Card({ children, style }) {
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, ...style }}>
      {children}
    </div>
  );
}
export function ChartCard({ title, children, style }) {
  return (
    <Card style={{ padding: '20px 16px', ...style }}>
      <div style={{ fontSize: 10, color: MUTED, letterSpacing: 1.5, marginBottom: 16, textTransform: 'uppercase' }}>{title}</div>
      {children}
    </Card>
  );
}

// ── KPI CARD ───────────────────────────────────────────────────────────────
export function KPICard({ label, value, sub, color = GOLD, style }) {
  return (
    <Card style={{ padding: '20px 24px', ...style }}>
      <div style={{ color: MUTED, fontSize: 9, letterSpacing: 2, marginBottom: 6 }}>{label.toUpperCase()}</div>
      <div style={{ color, fontSize: 22, fontWeight: 500, fontFamily: "'DM Mono', monospace", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ color: MUTED, fontSize: 10, marginTop: 6 }}>{sub}</div>}
    </Card>
  );
}

// ── SECTION TITLE ──────────────────────────────────────────────────────────
export function SectionTitle({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
      <div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: TEXT, margin: 0 }}>{title}</h2>
        {sub && <p style={{ color: MUTED, fontSize: 11, margin: '4px 0 0', letterSpacing: 1 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ── EMPTY STATE ────────────────────────────────────────────────────────────
export function EmptyState({ text = 'No data yet', icon = '◇' }) {
  return (
    <div style={{ textAlign: 'center', padding: '56px 0', color: MUTED }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>{icon}</div>
      <div style={{ fontSize: 12, letterSpacing: 1 }}>{text}</div>
    </div>
  );
}

// ── BADGE ──────────────────────────────────────────────────────────────────
export function Badge({ children, color = MUTED }) {
  return (
    <span style={{
      background: `${color}18`, color, border: `1px solid ${color}40`,
      borderRadius: 4, padding: '2px 8px', fontSize: 10, letterSpacing: 0.5,
    }}>{children}</span>
  );
}

// ── TABLE ──────────────────────────────────────────────────────────────────
export function Table({ headers, children, style }) {
  return (
    <Card style={{ overflow: 'hidden', ...style }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} style={{
                padding: '10px 14px', textAlign: 'left', fontSize: 9,
                color: MUTED, letterSpacing: 1.5, textTransform: 'uppercase',
                borderBottom: `1px solid ${BORDER}`, fontWeight: 400,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Card>
  );
}
export function TR({ children, onClick }) {
  return (
    <tr onClick={onClick} style={{ borderBottom: `1px solid #161D28`, cursor: onClick ? 'pointer' : 'default', transition: 'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{children}</tr>
  );
}
export function TD({ children, style }) {
  return <td style={{ padding: '10px 14px', color: TEXT, ...style }}>{children}</td>;
}

// ── TRANSACTION TABLE ──────────────────────────────────────────────────────
export function TransactionTable({ rows, onDelete, compact = false }) {
  if (!rows || rows.length === 0) return <EmptyState text="No transactions found" />;
  const headers = [
    'Date', 'Direction', 'Channel', 'Category', 'Description',
    'Qty', 'Unit Price', 'Total', 'Vendor',
    ...(compact ? [] : ['Metal', 'Weight', 'Source', 'Notes']),
    ...(onDelete ? [''] : []),
  ];
  return (
    <Table headers={headers}>
      {rows.map((t) => (
        <TR key={t.id}>
          <TD>{t.date}</TD>
          <TD style={{ color: t.direction === 'Sale' ? GREEN : RED }}>{t.direction}</TD>
          <TD style={{ color: MUTED }}>{t.channel}</TD>
          <TD>{t.category}</TD>
          <TD style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</TD>
          <TD>{t.qty}</TD>
          <TD>{fmtDec(t.unitPrice)}</TD>
          <TD style={{ color: GOLD, fontWeight: 500 }}>{fmt(t.totalAmount)}</TD>
          <TD style={{ color: MUTED }}>{t.vendor}</TD>
          {!compact && <TD>{t.metal}</TD>}
          {!compact && <TD style={{ color: MUTED }}>{t.weight ? `${t.weight} oz` : '—'}</TD>}
          {!compact && <TD><span style={{ fontSize: 9, color: MUTED, letterSpacing: 1 }}>{t.source === 'invoice' ? '📄 INV' : '✏️ MANUAL'}</span></TD>}
          {!compact && <TD style={{ color: MUTED, fontSize: 10, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.notes}</TD>}
          {onDelete && (
            <TD><DangerBtn onClick={() => onDelete(t.id)}>✕</DangerBtn></TD>
          )}
        </TR>
      ))}
    </Table>
  );
}

// ── MODAL ──────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 700 }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 13, color: GOLD, letterSpacing: 1 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

// ── METALS TICKER ──────────────────────────────────────────────────────────
export function MetalsTicker({ prices }) {
  const metals = [
    { key: 'Gold',      sym: 'XAU', color: '#C9A84C' },
    { key: 'Silver',    sym: 'XAG', color: '#A8B8C8' },
    { key: 'Platinum',  sym: 'XPT', color: '#D8D8F0' },
    { key: 'Palladium', sym: 'XPD', color: '#C0A8D8' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, background: '#0D1117', padding: '8px 20px', borderRadius: 8, border: `1px solid ${BORDER}`, flexWrap: 'wrap' }}>
      {metals.map((m) => (
        <div key={m.key} style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ color: m.color, fontSize: 9, fontWeight: 500, letterSpacing: 1.5 }}>{m.sym}</span>
          <span style={{ color: TEXT, fontSize: 13 }}>{prices[m.key] ? fmtDec(prices[m.key]) : '—'}</span>
          <span style={{ color: MUTED, fontSize: 9 }}>/oz</span>
        </div>
      ))}
      {prices.timestamp && (
        <span style={{ color: MUTED, fontSize: 9, marginLeft: 4 }}>
          {prices.ok ? '● ' : '○ '}{prices.timestamp}
        </span>
      )}
    </div>
  );
}
